import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import lazyLoad from "@/lib/lazyLoad";
import { trackPageview, bindAnalyticsLifecycle } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Eager: prerendered / SEO-critical entry routes (avoid Suspense flash over prerendered HTML)
import Home from "@/pages/Home";
import PropertyList from "@/pages/PropertyList";
import PropertyDetail from "@/pages/PropertyDetail";
import Login from "@/pages/Login";
import Concierge from "@/pages/Concierge";
import EntrustProperty from "@/pages/EntrustProperty";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import LuggageStorage from "@/pages/LuggageStorage";
import Legal from "@/pages/Legal";
import SeoLanding from "@/pages/SeoLanding";
import NotFound from "@/pages/NotFound";
import RevenueEstimator from "@/pages/RevenueEstimator";
import BusinessRental from "@/pages/BusinessRental";
import ResultsOwners from "@/pages/ResultsOwners";
import { SEO_SLUGS } from "@/data/seoPages";

// Lazy: non-prerendered deep-flow routes (code-split out of the initial bundle)
const Booking = lazyLoad(() => import("@/pages/Booking"));
const Confirmation = lazyLoad(() => import("@/pages/Confirmation"));
const Cancelled = lazyLoad(() => import("@/pages/Cancelled"));
const EcConfirmation = lazyLoad(() => import("@/pages/EcConfirmation"));
const TravelerSpace = lazyLoad(() => import("@/pages/TravelerSpace"));
const LuggageConfirmation = lazyLoad(() => import("@/pages/LuggageConfirmation"));
const BlogPost = lazyLoad(() => import("@/pages/BlogPost"));
const ResetPassword = lazyLoad(() => import("@/pages/ResetPassword"));
const AcceptInvite = lazyLoad(() => import("@/pages/AcceptInvite"));
const Marketplace = lazyLoad(() => import("@/pages/Marketplace"));
const BookingAIApp = lazyLoad(() => import("@/bookingai/BookingAIApp"));

const OwnerDashboard = lazyLoad(() => import("@/pages/owner/OwnerDashboard"));
const AdminLayout = lazyLoad(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazyLoad(() => import("@/pages/admin/AdminDashboard"));
const AdminProperties = lazyLoad(() => import("@/pages/admin/AdminProperties"));
const AdminCalendar = lazyLoad(() => import("@/pages/admin/AdminCalendar"));
const AdminPricing = lazyLoad(() => import("@/pages/admin/AdminPricing"));
const AdminPropertyEdit = lazyLoad(() => import("@/pages/admin/AdminPropertyEdit"));
const AdminReservations = lazyLoad(() => import("@/pages/admin/AdminReservations"));
const AdminOwners = lazyLoad(() => import("@/pages/admin/AdminOwners"));
const AdminPayments = lazyLoad(() => import("@/pages/admin/AdminPayments"));
const AdminPayouts = lazyLoad(() => import("@/pages/admin/AdminPayouts"));
const AdminExtras = lazyLoad(() => import("@/pages/admin/AdminExtras"));
const AdminAlerts = lazyLoad(() => import("@/pages/admin/AdminAlerts"));
const AdminContent = lazyLoad(() => import("@/pages/admin/AdminContent"));
const AdminLeads = lazyLoad(() => import("@/pages/admin/AdminLeads"));
const AdminLuggage = lazyLoad(() => import("@/pages/admin/AdminLuggage"));
const AdminCustomers = lazyLoad(() => import("@/pages/admin/AdminCustomers"));
const AdminAnalytics = lazyLoad(() => import("@/pages/admin/AdminAnalytics"));

function AnalyticsTracker() {
  const loc = useLocation();
  useEffect(() => { bindAnalyticsLifecycle(); }, []);
  useEffect(() => { trackPageview(loc.pathname); }, [loc.pathname]);
  useEffect(() => {
    const h = () => trackPageview(window.location.pathname);
    window.addEventListener("nj-cookies-accepted", h);
    return () => window.removeEventListener("nj-cookies-accepted", h);
  }, []);
  return null;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-nj-muted">Chargement…</div>;
  if (user === false) return <Navigate to="/connexion" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/logements" element={<PropertyList />} />
            <Route path="/location-entreprises" element={<BusinessRental />} />
            <Route path="/logements/:slug" element={<PropertyDetail />} />
            <Route path="/reserver/:slug" element={<Booking />} />
            <Route path="/reservation/confirmation" element={<Confirmation />} />
            <Route path="/reservation/annulee" element={<Cancelled />} />
            <Route path="/reservation/confirmee" element={<EcConfirmation />} />
            <Route path="/mon-sejour/:reference" element={<TravelerSpace />} />
            <Route path="/connexion" element={<Login />} />
            <Route path="/conciergerie" element={<Concierge />} />
            <Route path="/nos-services" element={<Concierge />} />
            <Route path="/notre-fonctionnement" element={<Concierge />} />
            <Route path="/confier-mon-logement" element={<EntrustProperty />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/decouvrir-rouen" element={<Blog />} />
            <Route path="/consigne" element={<LuggageStorage />} />
            <Route path="/consigne/confirmation" element={<LuggageConfirmation />} />
            <Route path="/decouvrir-rouen/:slug" element={<BlogPost />} />
            <Route path="/mentions-legales" element={<Legal type="mentions" />} />
            <Route path="/confidentialite" element={<Legal type="privacy" />} />
            <Route path="/cgv" element={<Legal type="cgv" />} />
            <Route path="/cookies" element={<Legal type="cookies" />} />

            <Route path="/estimation-revenus-airbnb-rouen" element={<RevenueEstimator />} />
            <Route path="/resultats-proprietaires" element={<ResultsOwners />} />
            <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
            {SEO_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<SeoLanding />} />
            ))}

            <Route path="/proprietaire" element={<Protected roles={["owner"]}><OwnerDashboard /></Protected>} />
            <Route path="/espace-vente" element={<Protected roles={["owner", "admin", "super_admin", "seller"]}><Marketplace /></Protected>} />
            <Route path="/rejoindre-vendeur" element={<AcceptInvite />} />

            {/* NJ Booking AI — back-office privé, invisible du site public */}
            <Route path="/booking-ai/*" element={<BookingAIApp />} />

            <Route path="/admin" element={<Protected roles={["super_admin", "admin"]}><AdminLayout /></Protected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="logements" element={<AdminProperties />} />
              <Route path="logements/nouveau" element={<AdminPropertyEdit />} />
              <Route path="logements/:id" element={<AdminPropertyEdit />} />
              <Route path="calendrier" element={<AdminCalendar />} />
              <Route path="tarification" element={<AdminPricing />} />
              <Route path="reservations" element={<AdminReservations />} />
              <Route path="proprietaires" element={<AdminOwners />} />
              <Route path="paiements" element={<AdminPayments />} />
              <Route path="reversements" element={<AdminPayouts />} />
              <Route path="options" element={<AdminExtras />} />
              <Route path="alertes" element={<AdminAlerts />} />
              <Route path="contenu" element={<AdminContent />} />
              <Route path="demandes" element={<AdminLeads />} />
              <Route path="clients" element={<AdminCustomers />} />
              <Route path="statistiques" element={<AdminAnalytics />} />
              <Route path="consigne" element={<AdminLuggage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
